import * as React from 'react';
import onClickOutside from 'react-onclickoutside';
import classnames from 'classnames';
import produce from 'immer';

import Button from '@components/Button';
import Checkbox from '@components/Checkbox';

import './style.scss';

interface IPicklistOptions {
  label: string;
  value: any;
  isChecked?: boolean;
  isDisabled?: boolean;
}

interface IPicklistWithHandlerProps {
  name: string;
  options: IPicklistOptions[];
  value: string;
  placeholder?: string;
  disabled?: boolean;
  multi?: boolean;
  identifier?: string;
  handler?: JSX.Element;
  noRowsMessage?: string;
  onBlur?: (e: any) => void;
  onFocus?: (e: any) => void;
  onChange: (e: any) => void;
  onOptionSelection: (e: any) => void;
  selectAll?: boolean;
  showHandler?: boolean;
  labelType?: LabelType;
}

interface IPicklistWithHandlerState {
  show: boolean;
  label: string;
  options: IPicklistOptions[];
  selectAll: boolean;
  isFocused: boolean;
}

class PicklistWithHandler extends React.Component<IPicklistWithHandlerProps, IPicklistWithHandlerState> {
  public static defaultProps = {
    name: '',
    value: '',
    placeholder: 'Select Option',
    options: null,
    disabled: false,
    identifier: '',
    handler: null,
    showHandler: true,
    multi: false,
    selectAll: false,
    noRowsMessage: 'No rows found',
    onBlur: () => null,
    onFocus: () => null,
    onChange: () => null,
    onOptionSelection: () => null,
    labelType: LabelType.SELECTED_NUMBER,
  };
  constructor(props: IPicklistWithHandlerProps) {
    super(props);

    this.state = {
      show: false,
      label: '',
      options: this.props.options,
      selectAll: false,
      isFocused: false,
    };
  }

  componentDidMount(): void {
    let selectedOption = null;
    if (this.props.options !== null) {
      selectedOption = this.props.options.filter((option) => option.value === this.props.value)[0];
    }
    this.setState({
      options: this.props.options,
      label: selectedOption ? selectedOption.label : null,
    });
  }

  componentDidUpdate(prevProps: Readonly<IPicklistWithHandlerProps>) {
    if (!prevProps.multi) {
      if (this.props.options !== prevProps.options || prevProps.value !== this.props.value) {
        const selectedOption = this.props.options.filter((option) => option.value === this.props.value)[0];
        if (selectedOption) {
          this.setState({
            label: selectedOption.label,
            options: this.props.options,
          });
        }
      }
    } else {
      if (this.props.options !== prevProps.options) {
        const selectedOptions = this.props.options.filter((option) => option.isChecked);
        this.setState({
          options: this.props.options,
          label: this.getLabel(selectedOptions),
          selectAll: this.props.options.filter((option) => !option.isDisabled).every((e) => e.isChecked),
        });
      }
    }
  }

  getLabel = (selectedOptions: IPicklistOptions[]): string => {
    if (this.props.labelType === LabelType.SELECTED_VALUE) {
      const constructedLabel =
        selectedOptions.length > 0 ? selectedOptions.map((option) => option.label).join(', ') : '';

      return constructedLabel;
    } else {
      return selectedOptions.length > 0 ? `${selectedOptions.length} ${this.props.identifier} selected` : '';
    }
  };

  handleClickOutside = (e: React.SyntheticEvent<MouseEvent>) => {
    this.setState((prevState) => ({
      ...prevState,
      show: false,
    }));
  };

  toggleList = (e: React.SyntheticEvent<any>) => {
    if (!this.props.disabled) {
      this.setState((prevState) => ({
        show: !prevState.show,
        isFocused: !prevState.show,
        options: this.props.options,
        selectAll: this.props.options.every((elem) => elem.isChecked),
      }));
    }
  };

  handleFocus = (e: React.SyntheticEvent<any>) => {
    if (!this.props.disabled) {
      this.setState((prevState) => ({
        isFocused: !prevState.isFocused,
      }));
    }
  };

  renderList = () => {
    const { options, multi, name, handler, selectAll } = this.props;
    if (options && options.length) {
      return (
        <ul
          className={classnames('picklist-with-handler__picklist-section', {
            'multi-picklist': multi,
          })}
          tabIndex={-1}
        >
          {multi && selectAll && (
            <li className="picklist-with-handler__top-handler multi">
              <Checkbox id="all" isChecked={this.state.selectAll} name="All" onChange={this.selectAll}>
                All
              </Checkbox>
              {handler}
            </li>
          )}
          {this.state.options.map((option, index) =>
            !multi ? (
              <li
                tabIndex={0}
                className={
                  option.isDisabled === true
                    ? 'picklist-with-handler__picklist-disabled'
                    : 'picklist-with-handler__picklist'
                }
                key={index}
                onClick={option.isDisabled === true ? undefined : this.selectOption(option)}
              >
                <span>{option.label}</span>
              </li>
            ) : (
              <li key={index} className="multi">
                <Checkbox
                  id={`${index}`}
                  isChecked={option.isChecked}
                  name={name}
                  disabled={option.isDisabled}
                  onChange={this.handleMultiSelect(option)}
                >
                  {option.label}
                </Checkbox>
              </li>
            )
          )}
        </ul>
      );
    }

    return null;
  };

  renderHandlerSection = () => {
    const { handler, multi, options, noRowsMessage } = this.props;

    return multi && options && options.length ? (
      <div tabIndex={0} className={classnames('picklist-with-handler__handler-section', 'row', 'user-actions')}>
        <Button onClick={this.toggleList} bsStyle={ButtonStyle.DEFAULT}>
          Cancel
        </Button>
        <Button onClick={this.applySelection} bsStyle={ButtonStyle.PRIMARY}>
          Apply
        </Button>
      </div>
    ) : (
      <div tabIndex={0} className={classnames('picklist-with-handler__handler-section')}>
        {handler || noRowsMessage}
      </div>
    );
  };

  selectAll = (e: React.SyntheticEvent<any>) => {
    this.setState(
      produce((draft: any) => {
        draft.options.forEach((elem) => {
          if (!elem.isDisabled) {
            elem.isChecked = !draft.selectAll;
          }
        });
        draft.selectAll = !draft.selectAll;
      }),
      () => {
        const selectedOptions = this.state.options.filter((elem) => elem.isChecked).map((selection) => selection.value);
        if (this.props.onOptionSelection) {
          this.props.onOptionSelection({
            target: { name: this.props.name, value: selectedOptions },
            currentTarget: { name: this.props.name, value: selectedOptions },
          });
        }
      }
    );
  };

  handleMultiSelect = (option: { label: string; value: any }) => (e: React.SyntheticEvent<any>) => {
    this.setState(
      produce((draft: any) => {
        draft.options.forEach((elem) => {
          if (elem.value === option.value) {
            elem.isChecked = !elem.isChecked;
          }
        });
      }),
      () => {
        const isAllSelected = this.state.options.every((elem) => elem.isChecked);
        const selectedOptions = this.state.options.filter((elem) => elem.isChecked).map((selection) => selection.value);
        if (isAllSelected) {
          this.selectAll(e);
        } else {
          this.setState({
            selectAll: false,
          });
        }
        if (this.props.onOptionSelection) {
          this.props.onOptionSelection({
            target: { name: this.props.name, value: selectedOptions },
            currentTarget: { name: this.props.name, value: selectedOptions },
          });
        }
      }
    );
  };

  applySelection = () => {
    const newValue = this.state.options.filter((elem) => elem.isChecked).map((elem) => elem.value);
    this.setState(
      (prevState) => ({
        ...prevState,
        show: false,
        label: newValue.length ? `${newValue.length} ${this.props.identifier} selected` : '',
      }),
      () =>
        this.props.onChange({
          target: { name: this.props.name, value: newValue },
          currentTarget: { name: this.props.name, value: newValue },
        })
    );
  };

  selectOption = (option: { label: string; value: any }) => (e: React.SyntheticEvent<any>) => {
    this.setState((prevState) => ({
      show: false,
      label: option.label,
    }));
    this.props.onChange({
      target: { name: this.props.name, value: option.value },
      currentTarget: { name: this.props.name, value: option.value },
    });
  };

  render() {
    const { placeholder, value, name, onBlur, onFocus, options } = this.props;

    const selectedOptions = options.filter((option) => option.isChecked);
    const label = this.getLabel(selectedOptions);

    return (
      <div
        className={classnames('picklist-with-handler', 'Select', {
          'has-value': this.state.label || label,
          'is-focused': this.state.isFocused,
          'is-open': this.state.show,
          'is-disabled': this.props.disabled,
        })}
      >
        <input type="hidden" name={name} value={value || ''} onBlur={onBlur} onFocus={onFocus} />
        {/** Predefined class */}
        <div
          tabIndex={0}
          className={classnames('picklist-with-handler__input-box', 'Select-control')}
          onClick={this.toggleList}
          onFocus={this.handleFocus}
        >
          {/** Predefined class */}
          <span className="Select-placeholder placeholder">{this.state.label || label || placeholder}</span>
          {/** Predefined class */}
          <div className="Select-arrow-zone">
            <span className="Select-arrow" />
          </div>
        </div>
        {this.state.show && (
          <div className="picklist-with-handler__list">
            {this.renderList()}
            {this.props.showHandler && this.renderHandlerSection()}
          </div>
        )}
      </div>
    );
  }
}

export default onClickOutside(PicklistWithHandler);
